import BuildIcon from '@mui/icons-material/Build'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import SearchIcon from '@mui/icons-material/Search'
import {
  Alert,
  Autocomplete,
  Box,
  Dialog,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { Conversation } from '@twilio/conversations'
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { ChatMessages } from '../../components/ChatServices/ChatMessages'
import { OutlinedBox } from '../../components/SharedUI'
import { TABLET_WIDTH_BREAKPOINT } from '../../config/breakPoints'
import { useSearchResultsCustomers } from '../../hooks'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  AuthContext,
  MERGED_CUSTOMER_CONVERSATION_UNIQUE_NAME,
  MERGED_REPAIR_ORDERS_CONVERSATION_UNIQUE_NAME,
  TwilioConversationsContext,
  WindowSizeContext
} from '../../providers'
import {
  useGetUnreadNotificationsByModifiedEntityQuery,
  useMarkNotificationAsReadMutation
} from '../../redux/api/notificationApi'
import { CustomerEntry } from '../../sdk'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import { CustomerListItem } from '../SearchWithAutocomplete/CustomerListItem'
import { ListItemLoading } from '../SearchWithAutocomplete/ListItemLoading'
import { NoOptionsText } from '../SearchWithAutocomplete/NoOptionsText'
import { SlideUpDialogTransition } from '../SharedUI/SlideUpDialogTransition'
import { ChatPageCard } from './ChatPageCard'
import { useChat } from './useChat'

export interface StateToPass {
  activeConversationId: string
  customerName: string | undefined
}

const conversationAgeComparisonFunction = (
  a: Conversation,
  b: Conversation
) => {
  if (!b.lastMessage && !a.lastMessage) {
    return 0
  }
  if (!b.lastMessage) {
    return -1
  }
  if (!a.lastMessage) {
    return 1
  }
  return (
    b.lastMessage.dateCreated!.getTime() - a.lastMessage.dateCreated!.getTime()
  )
}

export const ChatPageDetails: FC = () => {
  const { accessToken } = useAccessTokenAndShopId()
  const { width, height } = useContext(WindowSizeContext)
  const overTabletBreakpoint = width >= TABLET_WIDTH_BREAKPOINT

  const { currentUser } = useContext(AuthContext)
  const { connectionStatus, subscribedConversations } = useContext(
    TwilioConversationsContext
  )
  const [stateToPass, setStateToPass] = useState<StateToPass>()

  const {
    joinConversation,
    activeConversation,
    messages,
    userMap,
    messageMediaMap
  } = useChat(
    stateToPass?.activeConversationId ? stateToPass.activeConversationId : '',
    stateToPass?.customerName
  )

  const bottomRef = useRef<HTMLDivElement>(null)

  const joinedConversations = useRef<boolean>(false)

  const [markAsRead] = useMarkNotificationAsReadMutation()
  const { data: unreadNotifications } =
    useGetUnreadNotificationsByModifiedEntityQuery(
      {
        authorization: accessToken,
        modifiedEntity: [activeConversation?.uniqueName!]
      },
      { skip: !activeConversation?.uniqueName }
    )
  const unreadNotificationIds = useMemo(
    () => (unreadNotifications || []).map((notification) => notification.id!),
    [unreadNotifications]
  )

  useEffect(() => {
    if (!joinedConversations.current) {
      joinedConversations.current = true
      const mergedCustomerConversation = subscribedConversations.find(
        (conversation) =>
          conversation.uniqueName === MERGED_CUSTOMER_CONVERSATION_UNIQUE_NAME
      )
      const mergedRepairOrderConversation = subscribedConversations.find(
        (conversation) =>
          conversation.uniqueName ===
          MERGED_REPAIR_ORDERS_CONVERSATION_UNIQUE_NAME
      )
      if (mergedCustomerConversation) {
        mergedCustomerConversation.getMessages().then((messagePaginator) => {
          const conversationsToJoin = new Set(
            messagePaginator.items
              .map((message) => {
                if (
                  typeof message.attributes !== 'object' ||
                  !(
                    'original_conversation_unique_name' in message.attributes!
                  ) ||
                  typeof message.attributes
                    .original_conversation_unique_name !== 'string'
                ) {
                  return ''
                }
                return message.attributes.original_conversation_unique_name
              })
              .filter((conversationId) => conversationId !== '')
          )
          conversationsToJoin.forEach((conversationId) => {
            joinConversation(conversationId, false, false)
          })
        })
      }
      if (mergedRepairOrderConversation) {
        mergedRepairOrderConversation.getMessages().then((messagePaginator) => {
          const conversationsToJoin = new Set(
            messagePaginator.items
              .map((message) => {
                if (
                  typeof message.attributes !== 'object' ||
                  !(
                    'original_conversation_unique_name' in message.attributes!
                  ) ||
                  typeof message.attributes
                    .original_conversation_unique_name !== 'string'
                ) {
                  return ''
                }
                return message.attributes.original_conversation_unique_name
              })
              .filter((conversationId) => conversationId !== '')
          )
          conversationsToJoin.forEach((conversationId) => {
            joinConversation(conversationId, true, false)
          })
        })
      }
    }
  }, [joinConversation, accessToken, subscribedConversations])

  useEffect(() => {
    if (messages) {
      bottomRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'start'
      })
    }
  }, [messages, messageMediaMap])

  const [inputValue, setInputValue] = useState('')
  const {
    matchingCustomers,
    loading: loadingCustomers,
    errorMessage: errorMessageCustomers
  } = useSearchResultsCustomers(inputValue)

  const handleClick = useCallback(
    (option: CustomerEntry) => {
      const customerName = formatName(option.information)

      joinConversation(option.id!, false, true)
      setStateToPass({
        activeConversationId: option.id!,
        customerName
      })
    },
    [joinConversation]
  )

  const searchCustomerBox = (
    <Autocomplete
      id="customerChatSearch"
      open={inputValue !== ''}
      loading={loadingCustomers}
      inputValue={inputValue}
      filterOptions={(options) => options}
      onInputChange={(_e: unknown, newValue) => setInputValue(newValue || '')}
      renderOption={(_props, option: CustomerEntry) => (
        <CustomerListItem
          key={option.id}
          option={option}
          handleClick={handleClick.bind(null, option)}
        />
      )}
      options={[...matchingCustomers]}
      getOptionLabel={(option) => {
        return formatName(option.information)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          error={!!errorMessageCustomers}
          placeholder="Search customers"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'primary.main' }} />
              </InputAdornment>
            )
          }}
        />
      )}
      noOptionsText={<NoOptionsText errorMessage={errorMessageCustomers} />}
      loadingText={<ListItemLoading />}
    />
  )

  const customerConversations = subscribedConversations.filter((conversation) =>
    conversation.friendlyName?.includes('Customer:')
  )
  const repairOrderConversations = subscribedConversations.filter(
    (conversation) => conversation.friendlyName?.includes('Repair-Order:')
  )
  const getRecentConversations = (conversations: Conversation[]) => {
    return conversations.sort((a, b) => conversationAgeComparisonFunction(a, b))
  }

  const displayConversations = (conversations: Conversation[]) => {
    return conversations.map((conversation) => (
      <ChatPageCard
        key={conversation.sid}
        setStateToPass={setStateToPass}
        activeConversationUniqueName={
          activeConversation?.uniqueName || undefined
        }
        conversation={conversation}
      />
    ))
  }

  const displayRecentCustomerConversations = displayConversations(
    getRecentConversations(customerConversations)
  )

  const displayRecentRepairOrderConversations = displayConversations(
    getRecentConversations(repairOrderConversations)
  )

  const menuItems = (
    <Stack spacing={1}>
      {searchCustomerBox}
      <Paper variant="outlined">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          p={1}
          spacing={1}
          sx={{ borderRadius: 1, background: '#F9EFCC' }}>
          <PersonIcon fontSize="medium" />
          <Typography variant="h4">Customers</Typography>
        </Stack>
        <Divider sx={{ width: '100%' }} />
        <Box sx={{ minHeight: 100, maxHeight: 275, overflowY: 'scroll' }}>
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="flex-start"
            spacing={0}
            sx={{ width: '100%' }}>
            {displayRecentCustomerConversations}
          </Stack>
        </Box>
      </Paper>
      <Paper variant="outlined">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          p={1}
          spacing={1}
          sx={{ borderRadius: 1, background: '#CFECD5' }}>
          <BuildIcon fontSize="medium" />
          <Typography variant="h4">Repair Orders</Typography>
        </Stack>
        <Divider sx={{ width: '100%' }} />
        <Box sx={{ minHeight: 100, maxHeight: 275, overflowY: 'scroll' }}>
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="flex-start"
            spacing={0}
            sx={{ width: '100%' }}>
            {displayRecentRepairOrderConversations}
          </Stack>
        </Box>
      </Paper>
    </Stack>
  )

  useEffect(() => {
    if (stateToPass && activeConversation) {
      activeConversation.setAllMessagesRead()
      if (unreadNotificationIds.length > 0) {
        markAsRead({
          authorization: accessToken,
          requestBody: unreadNotificationIds
        })
      }
    }
  }, [
    stateToPass,
    activeConversation,
    messages,
    accessToken,
    markAsRead,
    unreadNotificationIds
  ])

  const openChat =
    stateToPass && activeConversation ? (
      <Paper variant="outlined">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          p={1}
          sx={{
            borderRadius: 1,
            background: activeConversation.friendlyName?.includes('Customer')
              ? '#F9EFCC'
              : '#CFECD5'
          }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            {activeConversation.friendlyName?.includes('Customer') ? (
              <PersonIcon fontSize="medium" />
            ) : (
              <BuildIcon fontSize="medium" />
            )}
            <Typography variant="h4">
              {activeConversation.friendlyName}
            </Typography>
          </Stack>
          <IconButton onClick={() => setStateToPass(undefined)}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <ChatMessages
          userId={currentUser?.id}
          messages={messages}
          userMap={userMap}
          messageMediaMap={messageMediaMap}
          activeConversation={activeConversation}
          disableDropzone={
            !(activeConversation && connectionStatus === 'connected')
          }
          bottomRef={bottomRef}
          maxHeight={overTabletBreakpoint ? 560 : height - 120}
          minHeight={overTabletBreakpoint ? 560 : height - 120}
        />
      </Paper>
    ) : (
      <Alert severity="info">
        No Conversations Open - Conversations with a red bubble next to them
        indicate that they have unread messages
      </Alert>
    )

  return (
    <OutlinedBox title="Conversations">
      {overTabletBreakpoint ? (
        <Grid container>
          <Grid item xs={4} key="Chats Available">
            {menuItems}
          </Grid>
          <Grid item xs={8} key="Open Chats">
            <Box pl={2}>{openChat}</Box>
          </Grid>
        </Grid>
      ) : (
        <Stack spacing={2}>
          {menuItems}
          <Dialog
            fullScreen
            open={stateToPass !== undefined}
            onClose={() => {
              setStateToPass(undefined)
            }}
            TransitionComponent={SlideUpDialogTransition}
            keepMounted>
            {openChat}
          </Dialog>
        </Stack>
      )}
    </OutlinedBox>
  )
}
